import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MarketingLayout from './MarketingLayout';
import HomePage from '../Home/HomePage';
import { GlobalLayout, NewChatPage, fromAppCore } from '../../appChunks';
import { hasBlogVoiceHire } from '../../utils/blogVoiceHandoff';

const ActivationFork = fromAppCore('ActivationFork');

// Activation Fork session tokens (CEL-1341):
// - GRACE: set when the impression is stamped; recovers a mid-selection REFRESH
//   (a minutes-scale need). TIME-BOUND since CEL-1793: Chrome session restore
//   keeps sessionStorage alive for weeks in a long-lived tab, so an
//   unexpired-forever grace turned "shown once, never answered" into
//   "re-shows on every visit to '/'" (sidebar New Agent clicks included).
//   Value is JSON {uid, ts}; legacy bare-uid tokens read as expired.
// - DONE: set on action/skip/key-created. Overrides everything except preview —
//   the in-memory profile still carries the stale pre-impression
//   should_show=true, so without this a same-session return to "/" would
//   re-show a fork the user already answered.
const FORK_GRACE_KEY = 'cc_fork_grace_v1';
const FORK_DONE_KEY = 'cc_fork_done_v1';
const FORK_GRACE_TTL_MS = 30 * 60 * 1000; // 30 min — mid-selection refresh recovery only

/**
 * RootRoute — the home route ("/") and, with `surface="chat"`, the "/new" alias.
 *  - "/new" (any auth state) → redirects to "/" with the query string kept. It was the real New Chat
 *    route 2026-09-12 → 09-16 while "/" was the hire page; New Chat is back at "/" since 2026-09-16
 *    (Nitish: existing users ran into trouble with the hire-page front door), and the alias keeps the
 *    links minted in that window working (emails, bookmarks, Stripe return paths). Nothing new should
 *    link to "/new"; a chat is "/", a hire is "/ai_employee".
 *  - Logged OUT → "/" renders the three-product homepage hub (AI-Employee-led: hero +
 *    benchmark + AIE/Super-Agent/A2A sections) inside the dark MarketingLayout. The full AI-Employee
 *    deep-dive lives at /ai-employees (self-canonical).
 *  - Logged IN, NEW signup (Activation Fork gate, CEL-1341) → the one-time
 *    full-screen "What brings you here?" fork INSTEAD of the app home — no
 *    sidebar, no app shell, from ANY entry path (welcome Continue, Stripe
 *    return, direct nav, the "/new" alias too). Server-computed `should_show_activation_fork` +
 *    session grace/done tokens; `?fork_preview=1` forces a stampless preview
 *    render for QC. After action/skip: the normal app forever.
 *  - Logged IN otherwise → "/" is NEW CHAT inside GlobalLayout, for every account. New AI Employee
 *    lives at its own route, /ai_employee (the sidebar row, the marketing hire CTAs). Every "go home"
 *    keeps "/". The fork's own submits navigate into the created chat / employee; its Skip lands here.
 *    All lazy from the app-core chunk (CEL-1086 bundle split).
 */
const RootRoute = ({ surface = 'home' }) => {
  const { currentUser, userProfile, profileLoading } = useAuth();
  const location = useLocation();
  // Local dismissal: skip-to-home renders the normal home without a route
  // change (navigation to other destinations unmounts us anyway).
  const [forkDismissed, setForkDismissed] = useState(false);

  if (surface === 'chat') {
    // "/new" is an alias of "/" (see the header). Redirect BEFORE the fork gate so a first-timer sees
    // the fork on "/" like everyone else; logged out this is what the old NewRedirect did.
    return <Navigate to={`/${location.search}`} replace />;
  }

  if (currentUser) {
    const preview = new URLSearchParams(location.search).get('fork_preview') === '1';
    // Tokens are uid-SCOPED: a fork shown to account A in this tab must never
    // re-show for account B after a same-session login switch (the
    // cross-account leak Nitish hit 2026-08-18). Grace is additionally
    // TIME-BOUND (CEL-1793) — see the header comment. Malformed/legacy tokens
    // read as expired; the DONE token stays a bare uid (immortal is the safe
    // direction for "already answered").
    let grace = false;
    let done = false;
    try {
      const uid = currentUser?.uid;
      done = !!uid && sessionStorage.getItem(FORK_DONE_KEY) === uid;
      if (uid && !done) {
        const raw = sessionStorage.getItem(FORK_GRACE_KEY);
        const tok = raw ? JSON.parse(raw) : null;
        grace = !!tok && tok.uid === uid && typeof tok.ts === 'number'
          && (Date.now() - tok.ts) < FORK_GRACE_TTL_MS;
      }
    } catch (e) { /* noop — malformed token reads as expired */ }
    // Trust the server flag only when the profile is NOT mid-refresh: the
    // localStorage cache can carry a stale pre-impression should_show=true
    // into a NEW session (stamped server-side after the cache was written),
    // which would re-show a fork the user already saw — violating show-once.
    // The grace token still covers the legitimate same-session refresh.
    // "Working with a team?" (Oct 2 2026): the fork renders OUTSIDE GlobalLayout, so a new signup with a pending team
    // question never reached the screen (GlobalLayout swaps its children for TeamChoiceScreen). Hold the fork until the
    // answer: render the shell, which shows the team screen; the answer clears team_offer and "/" then shows the fork.
    if (userProfile?.team_offer?.domain && !preview) {
      return (
        <GlobalLayout>
          <NewChatPage />
        </GlobalLayout>
      );
    }
    // Blog voice "Start this" (CEL-3260): a joined account that still holds the carried brief (blogVoiceHandoff stash, set on
    // the hire URL before a signup detour) goes to the hire page, which reads and consumes it. After the team question, before
    // the fork: consent and the team screen both land here on "/".
    if (!preview && userProfile?.onboarding_status === 'JOINED' && hasBlogVoiceHire()) {
      return <Navigate to="/ai_employee" replace />;
    }
    const serverShow = !!userProfile?.should_show_activation_fork && !profileLoading;
    const showFork =
      !forkDismissed &&
      (preview || (!done && (serverShow || (grace && !!userProfile))));

    if (showFork) {
      return <ActivationFork preview={preview} onDone={() => setForkDismissed(true)} />;
    }

    // First-ever profile load (no cache yet): the fork-or-home decision is
    // unknown. Hold a blank page-colored frame instead of flashing the app
    // shell (the no-sidebar-flash rule) — resolves in one profile fetch.
    if (!userProfile && profileLoading) {
      return <div style={{ minHeight: '100vh', background: 'var(--cc-bg)' }} />;
    }

    return (
      <GlobalLayout>
        <NewChatPage />
      </GlobalLayout>
    );
  }

  return (
    <MarketingLayout>
      <HomePage />
    </MarketingLayout>
  );
};

export default RootRoute;
